import Vue from "vue";
import store from "../store";
import VueRouter, { NavigationGuardNext, Route, RouteConfig } from "vue-router";
import { authenticator } from "@/util/authenticator";
import { Roles } from "@/interfaces/entities/User";

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
    {
        path: "/",
        redirect: "/your-store/dashboard"
    },
    {
        path: "/starter/login",
        meta: { public: true }
    },
    {
        path: "/starter/registration",
        meta: { public: true }
    },
    {
        path: "/data-packages",
        name: "Data plans",
        meta: { roles: [Roles.ADMIN, Roles.SALES] },
        component: () => import(/* webpackChunkName: "data-packages" */ "../components/pages/AddPackages.vue")
    },
    {
        path: "/add-credits/checkout",
        name: "Add Credits Checkout",
        component: () => import(/* webpackChunkName: "add-credits-checkout" */ "../components/pages/ManualCheckout.vue")
    },
    {
        path: "/data-packages/checkout",
        name: "Checkout",
        component: () => import(/* webpackChunkName: "checkout" */ "../components/pages/Checkout.vue")
    },
    {
        path: "/data-packages/checkout/success",
        name: "Checkout Success",
        component: () => import(/* webpackChunkName: "checkout-success" */ "../components/pages/CheckoutSuccess.vue")
    },
    {
        path: "/your-store",
        name: "Your Store",
        meta: { headerHeight: "213px" },
        component: () => import(/* webpackChunkName: "your-store" */ "../components/pages/YourStore.vue")
    },
    {
        path: "/your-store/:page",
        name: "Your Store Page",
        meta: { headerHeight: "213px" },
        component: () => import(/* webpackChunkName: "your-store-page" */ "../components/pages/YourStore.vue")
    },
    {
        path: "/your-store/history/:id",
        name: "Your Store History ID",
        meta: { headerHeight: "160px" },
        component: () => import(/* webpackChunkName: "your-store-history-id" */ "../components/pages/HistoryDetails.vue")
    },
    {
        path: "/your-store/:page/:id",
        name: "Your Store Page ID",
        meta: { headerHeight: "213px" },
        component: () => import(/* webpackChunkName: "your-store-page-id" */ "../components/pages/YourStore.vue")
    },
    {
        path: "/store",
        meta: { headerHeight: "160px", roles : [Roles.ADMIN, Roles.DEVELOPER] },
        name: "POS Store",
        component: () => import(/* webpackChunkName: "pos-store" */ "../components/pages/POSStore.vue")
    },
    {
        path: "/store/:type",
        meta: { headerHeight: "160px", roles : [Roles.ADMIN, Roles.DEVELOPER] },
        name: "POS Store Customer",
        component: () => import(/* webpackChunkName: "pos-store" */ "../components/pages/POSStoreCustomer.vue")
    },
    {
        path: "/store/:type/packages",
        meta: { headerHeight: "160px", roles : [Roles.ADMIN, Roles.DEVELOPER] },
        name: "POS Store Package",
        component: () => import(/* webpackChunkName: "pos-store" */ "../components/pages/POSStoreProducts.vue")
    },
    {
        path: "/store/:type/packages/:id",
        meta: { headerHeight: "160px", roles : [Roles.ADMIN, Roles.DEVELOPER] },
        name: "POS Store Package Sell",
        component: () => import(/* webpackChunkName: "pos-store" */ "../components/pages/POSStorePackage.vue")
    },
    {
        path: "/api",
        name: "API",
        meta: { headerHeight: "213px", roles: [Roles.DEVELOPER, Roles.ADMIN] },
        component: () => import(/* webpackChunkName: "api" */ "../components/pages/APIDashboard.vue")
    },
    {
        path: "/api/specifications",
        name: "API Specifications",
        meta: { public: true, headerHeight: "173px" },
        component: () => import(/* webpackChunkName: "api-specifications" */ "../components/pages/APISpecifications.vue")
    },
    {
        path: "/sdk",
        name: "SDK",
        meta: { headerHeight: "213px", roles: [Roles.ADMIN, Roles.DEVELOPER] },
        component: () => import(/* webpackChunkName: "sdk" */ "../components/pages/SDKDashboard.vue")
    },
    {
        path: "/sdk/:page",
        name: "SDK Page",
        meta: { headerHeight: "213px", roles: [Roles.ADMIN, Roles.DEVELOPER] },
        component: () => import(/* webpackChunkName: "your-store-page" */ "../components/pages/SDKDashboard.vue")
    },
    {
        path: "/credits",
        name: "Credit Page",
        meta: { headerHeight: "213px", roles: [Roles.ADMIN, Roles.BILLING] },
        component: () => import(/* webpackChunkName: "credits-page" */ "../components/pages/CreditsPage.vue")
    },
    {
        path: "/pricing",
        name: "Pricing Page",
        meta: { headerHeight: "213px", roles: [Roles.ADMIN, Roles.SALES, Roles.BILLING] },
        component: () => import(/* webpackChunkName: "pricing-page" */ "../components/pages/PricingPage.vue")
    },
    {
        path: "/esim/profile",
        name: "ESIM Installation",
        meta: { public: true },
        component: () => import(/* webpackChunkName: "esim-installation" */ "../components/pages/ESimInstallation.vue")
    },
    {
        path: "/customer/profile",
        name: "Customer Profile",
        meta: { public: true, headerHeight: "160px" },
        component: () => import(/* webpackChunkName: "customer-profile" */ "../components/pages/CustomerProfile.vue")
    },
    {
        path: "/esim-shop/profile",
        name: "Shopify Profile",
        meta: { public: true, headerHeight: "120px" },
        component: () => import(/* webpackChunkName: "customer-profile" */ "../components/pages/ESimShopProfile.vue")
    },
    {
        // Branding playground: renders the real CustomerProfile / ESimInstallation components
        // against fixture data, so resellers can see what their configuration will look like.
        path: "/profile-customization/demo",
        name: "White-label Demo",
        meta: { public: true },
        component: () => import(/* webpackChunkName: "whitelabel-demo" */ "../components/pages/WhitelabelDemo.vue")
    },
    {
        path: "/account",
        name: "Account",
        component: () => import(/* webpackChunkName: "account" */ "../components/pages/Account.vue")
    },
    {
        path: "/account/:page",
        name: "Account Page",
        component: () => import(/* webpackChunkName: "account" */ "../components/pages/Account.vue")
    },
    {
        path: "/account/:page/:id",
        name: "Account Page Detail",
        component: () => import(/* webpackChunkName: "account" */ "../components/pages/Account.vue")
    },
    {
        path: "/terms-and-conditions",
        name: "Terms & Conditions",
        meta: { public: true },
        component: () => import(/* webpackChunkName: "tandc" */ "../components/pages/TermsAndConditionsPage.vue")
    },
    {
        path: "/privacy-policy",
        name: "Privacy Policy",
        meta: { public: true },
        component: () => import(/* webpackChunkName: "privacy" */ "../components/pages/PrivacyPage.vue")
    },
    {
        path: "/unauthorized",
        name: "Unauthorized",
        meta: { public: true },
        component: () => import(/* webpackChunkName: "unauthorized" */ "../components/pages/Unauthorized.vue")
    },
    {
        path: "/invitation",
        name: "Invitation",
        meta: { public: true },
        component: () => import(/* webpackChunkName: "invitation" */ "../components/pages/Invitation.vue")
    },
    {
        path: "*",
        redirect: "/"
    }
];

const router = new VueRouter({
    routes
});
declare let gtag: any;
declare let gaMeasurementId: string;

function sendGoogleAnalyticsEvent(route: Route) {
    gtag("event", "page_view", {
        page_title: route.name,
        page_location: "Tunz Gigastore",
        page_path: route.path,
        send_to: gaMeasurementId
    });
}

router.beforeEach((to: Route, from: Route, next: NavigationGuardNext<Vue>) => {
    sendGoogleAnalyticsEvent(to);
    window.scrollTo(0, 0);
    if (handleAuthRedirects(to, next)) {
        return;
    }
    if (!to.meta.public) {
        authenticator
            .isAuthenticated()
            .then((authenticated: boolean) => {
                if (authenticated) {
                    console.log("[router] User is authenticated, checking roles...", to.meta);
                    if (to.meta.roles) {
                        if (store.getters.hasAnyRole(to.meta.roles)) {
                            next();
                        } else {
                            next("/unauthorized");
                        }
                    } else {
                        next();
                    }
                } else {
                    next("/starter/login");
                }
            })
            .catch(() => {
                next("/starter/login");
            });
    } else {
        next();
    }
});

router.afterEach((to: Route, from: Route) => {
    store.commit("SET_PREVIOUS_VIEW", "");
    store.commit("SET_HEADER_HEIGHT", to.meta?.headerHeight ?? "213px");
    document.body.style.position = "relative";
    if (to.path !== from.path) document.title = process.env.VUE_APP_THEME_TITLE || "";
});

function handleAuthRedirects(to: Route, next: NavigationGuardNext<Vue>): boolean {
    try {
        const path = to.path.toLowerCase().trim();
        if (path === "/starter/login") {
            // check if already logged in
            authenticator
                .isAuthenticated()
                .then((authenticated: boolean) => {
                    if (authenticated) {
                        next("/your-store/dashboard");
                    } else {
                        authenticator.signIn();
                    }
                })
                .catch(() => {
                    authenticator.signIn();
                });
            return true;
        }
        else if (path === "/starter/registration") {
            authenticator.registerUser();
            return true;
        }
    }
    catch (ex) {
        console.error(ex);
    }
    return false;
}

export default router;
